import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { Router } from "@angular/router";

import { Observable, of, Subscription } from "rxjs";
import { get } from "lodash";
import * as XLSX from "xlsx";
import { numToSSColumn } from "../../../shared/utils/excelUtils";
import { TranslateService } from "@ngx-translate/core";

import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatDialog } from "@angular/material/dialog";

import {
  TableConfig,
  FieldType,
  DefaultPage,
  TableEvent,
  ColumnHidden,
  ExportEvent,
  ExportFormat,
  AdvancedFilter,
  ColumnSorterInfo,
  CategoriesPropertiesPrefixes,
  FilterName,
  ExportLimit,
} from "../../../models/custom-table/custom-table.model";
import { SnackbarService } from "../../services/snackbar/snackbar.service";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { map } from "rxjs/operators";
import { ManageFileService } from "../../services/manage-files/manage-files.service";

@Component({
  selector: "app-custom-table",
  templateUrl: "./custom-table.component.html",
  styleUrls: ["./custom-table.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CustomTableComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @Input() tableTitle: string;
  @Input() set tableConfiguration(tableConfig: TableConfig[]) {
    this.tableConfig = tableConfig;
    this.setColumns();
  }
  @Input() columnSorterInfo: ColumnSorterInfo[];
  @Input() set loading(isLoading: boolean) {
    this.isLoading = isLoading;
  }
  @Input() set tableData(data: any[]) {
    this.setTable(data, this.tableConfig);
    this.setSearchTooltip(this.tableConfig);
  }
  @Input() set excelData(excelData: any[]) {
    this.exportExcel(excelData, this.selectedExportFormat);
  }
  @Input() tableLength: number;
  @Input() formPath: string;
  @Input() creationFormPath: string;
  @Input() dialogComponent: any;
  @Input() dialogData: object;
  @Input() showHeader: boolean = true;
  @Input() canOpen: boolean = true;
  @Input() canCreate: boolean = true;
  @Input() canDuplicate: boolean = true;
  @Input() searchLocal: boolean = false;
  @Input() canGenerate: boolean = false;
  @Input() canAdvanceFilter: boolean = true;
  @Input() canDateFilter: boolean = false;
  @Input() canOptionsFilter: boolean = false;
  @Input() canDownload: boolean = true;
  @Input() canSelect: boolean = false;
  @Input() dateFilterSelectedMode: string;
  @Input() dateFilterInitialLoad: boolean = false;
  @Input() optionsList: { code: string; description: string }[];
  @Input() optionField: string;
  @Input() searchFilter: boolean = true;
  @Input() pagination: boolean = true;
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];

  @Output()
  public getTableData: EventEmitter<TableEvent> = new EventEmitter<TableEvent>();
  @Output()
  public getExportData: EventEmitter<ExportEvent> = new EventEmitter<ExportEvent>();
  @Output()
  public saveTableConfig: EventEmitter<ColumnSorterInfo[]> = new EventEmitter<
    ColumnSorterInfo[]
  >();
  @Output()
  public openRowData: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  public downloadRowData: EventEmitter<string> = new EventEmitter<string>();
  @Output() public getSearchTerms: EventEmitter<any> = new EventEmitter<any>();
  @Output() public getPage: EventEmitter<any> = new EventEmitter<any>();
  @Output() public getAdvancedFilter: EventEmitter<any> =
    new EventEmitter<any>();
  @Output() public getSort: EventEmitter<any> = new EventEmitter<any>();
  tableConfig: TableConfig[];
  @Output()
  dataSource: MatTableDataSource<any[]>;
  @Output() public selectedRowEmit: EventEmitter<Object> = new EventEmitter<Object>();
  displayedColumns: string[];
  initDisplayedColumns: string[];
  allColumns: string[];
  allColumnNames: string[];
  columnsHidden: ColumnHidden[];
  pinnedColumns: string[];
  isLoading = false;
  count: number = 0;
  fieldType = FieldType;
  exportFormat = ExportFormat;
  exportLimit = ExportLimit;
  selectedExportFormat: ExportFormat;
  categoriesPropertiesPrefixes = CategoriesPropertiesPrefixes;

  searchTerms: string[] = [];
  searchTooltipText: string;

  advancedFilter: AdvancedFilter[] = [];
  dateFilter: AdvancedFilter[] = [];
  optionFilter: AdvancedFilter[] = [];

  defaultPageSize = DefaultPage.PAGE_SIZE;

  langSubscription: Subscription;

  public selectedRow: object = {};

  thumbnailCache: Record<string, Observable<SafeUrl>> = {};

  public locale: string = localStorage.getItem("locale");

  constructor(
    private router: Router,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private manageFileService: ManageFileService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.setSearchTooltip(this.tableConfig);
    });
  }

  public checkboxChanged(event: MatCheckboxChange, rowInfo: any): void {
    if (event.checked) {
      this.selectedRow = this.formatSelectedRowObject(rowInfo);
    } else {
      this.selectedRow = {};
    }
  
    this.selectedRowEmit.emit({ row: this.selectedRow });
  }

  private formatSelectedRowObject(flatObject: any): any {
    const nestedObject = {};
  
    Object.keys(flatObject).forEach(key => {
      const keys = key.split('.');
      keys.reduce((acc, part, index) => {
        if (index === keys.length - 1) {
          acc[part] = flatObject[key];
        } else {
          acc[part] = acc[part] || {};
        }
        return acc[part];
      }, nestedObject);
    });
  
    return nestedObject;
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  setColumns() {
    this.allColumns = this.setDisplayedColumns(this.tableConfig);
    this.allColumnNames = this.setDisplayedColumnNames(this.tableConfig);
    this.columnsHidden = this.setDisplayedColumnsHidden(this.tableConfig);
    this.initDisplayedColumns = this.setDisplayedColumns(this.tableConfig);
  }

  setTable(data: any[], tableConfig: TableConfig[]) {
    this.dataSource = new MatTableDataSource<any[]>(
      this.setDataSource(data, tableConfig)
    );
    this.displayedColumns =
      this.displayedColumns || this.setDisplayedColumns(tableConfig);
  }

  setDataSource(data: any, tableConfig: TableConfig[]) {
    if (!data) {
      return;
    }

    return data.map((dataEl: any) => {
      let treatedElement = {};
      tableConfig.forEach(({ field }) => {
        treatedElement = {
          ...treatedElement,
          [field]: field.includes(".")
            ? this.setField(dataEl, field)
            : dataEl[field],
        };
      });
      return treatedElement;
    });
  }

  setDisplayedColumns(config: TableConfig[]): string[] {
    return config.filter(({ outOfView }) => !outOfView).map(({ field }) => field);
  }

  setDisplayedColumnNames(config: TableConfig[]): string[] {
    return config.map(({ header }) => header);
  }

  setDisplayedColumnsHidden(config: TableConfig[]): ColumnHidden[] {
    return config.map(({ header, hidden }) => {
      return {
        header,
        hidden: hidden ? hidden : false,
      };
    });
  }

  setSearchTooltip(tableConfig: TableConfig[]) {
    this.translate
      .get("SimpleFilter.HintTitle")
      .subscribe((translatedTitle) => {
        const searchTooltipHint = translatedTitle;
        const searchableFields = tableConfig
          .filter(({ searchable }) => searchable)
          .map(({ header }) => this.translate.instant(header));
        this.searchTooltipText = `${searchTooltipHint}\n${searchableFields.join(
          "\n"
        )}`;
      });
  }

  getDates(dates: { startDate: string; endDate: string }) {
    const { startDate, endDate } = dates;
    const dateFilterCol = this.tableConfig.find((col) => col.dateFilter);

    const quickFiltersArr = [
      {
        column: dateFilterCol.field,
        fieldType: FieldType.DATE_TIME,
        operator: FilterName.GREATER_THAN_EQUAL,
        value: startDate,
      },
      {
        column: dateFilterCol.field,
        fieldType: FieldType.DATE_TIME,
        operator: FilterName.LESS_THAN_EQUAL,
        value: endDate,
      },
    ];

    if (this.dateFilter.length) {
      this.dateFilter = this.dateFilter.reduce((acc, cur, i) => {
        if (
          cur.column === dateFilterCol.field &&
          i < this.dateFilter.length - 1
        ) {
          return acc;
        }
        return startDate && endDate ? [...acc, ...quickFiltersArr] : acc;
      }, []);

      this.resetPaginator();
      this.getData();
      return;
    }

    this.dateFilter = [...quickFiltersArr];
    this.resetPaginator();
    this.getData();
  }

  getImageSource(value: string): Observable<SafeUrl | string> {
    if (!value || value.startsWith('http') || value.startsWith('data:image')) {
      return of(value);
    }
  
    if (!this.thumbnailCache[value]) {
      this.thumbnailCache[value] = this.manageFileService.getFileThumbnail(value).pipe(
        map(blob => {
          const url = URL.createObjectURL(blob);
          return this.sanitizer.bypassSecurityTrustUrl(url);
        })
      );
    }
  
    return this.thumbnailCache[value];
  }

  getWithOptionFilter(option: string) {
    const quickFiltersArr = [
      {
        column: this.optionField,
        fieldType: FieldType.STRING,
        operator: FilterName.EQUAL,
        value: option,
      },
    ];
    if (this.optionFilter.length) {
      this.optionFilter = this.optionFilter.reduce((acc, cur, i) => {
        if (
          cur.column === this.optionField &&
          i < this.optionFilter.length - 1
        ) {
          return acc;
        }
        return option ? [...acc, ...quickFiltersArr] : acc;
      }, []);

      this.resetPaginator();
      this.getData();
      return;
    }

    this.optionFilter = [...quickFiltersArr];
    this.resetPaginator();
    this.getData();
  }

  newRow() {
    if (this.dialogComponent) {
      this.openDialog(true);
      return;
    }

    if (this.creationFormPath) {
      this.router.navigate([`${this.creationFormPath}`]);
    } else {
      this.router.navigate([`${this.formPath}`]);
    }
  }

  setRouterLink(row: any) {
    if (this.dialogComponent) {
      return;
    }
  
    const idCol = this.tableConfig.find(col => col.identifier);
    const path = row.formPath || this.formPath;
  
    return [`/${path}`, row[idCol.field]];
  }

  openRow(row: any, dblClick?: boolean) {
    const idCol = this.tableConfig.find((col) => col.identifier);
    if (!idCol) {
      return;
    }
  
    if (this.dialogComponent) {
      this.openDialog(false, row);
      return;
    }

    const formPath = row.formPath || this.formPath;

    if (formPath) {
      if (dblClick) {
        this.router.navigate([formPath, row[idCol.field]]);
      } else {
        return [`/${formPath}`, row[idCol.field]];
      }
    } else {
      if (this.canSelect && dblClick) {
        this.selectedRow = this.formatSelectedRowObject(row);
        this.selectedRowEmit.emit({row: this.selectedRow, dblClick});
      } else {
        this.openRowData.emit(row[idCol.field]);
      }
    }
  }

  generateRow(row: any) {
    const idCol = this.tableConfig.find((col) => col.identifier);
    if (idCol) {
      this.openRowData.emit(row[idCol.field]);
    }
  }

  downloadRow(row: any) {
    const idCol = this.tableConfig.find((col) => col.identifier);
    if (idCol) {
      this.downloadRowData.emit(row[idCol.field]);
    }
  }

  duplicateRow(row: any) {
    if (this.dialogComponent) {
      this.openDialog(true, row);
      return;
    }

    const idCol = this.tableConfig.find((col) => col.identifier);
    if (idCol) {
      localStorage.setItem("duplicate", `${row[idCol.field]}`);
      this.router.navigate([this.formPath]);
    }
  }

  openDialog(isCreate: boolean, rowData?: object) {
    this.dialog.open(this.dialogComponent, {
      autoFocus: false,
      data: {
        ...this.dialogData,
        rowData,
        isCreate,
      },
    }).afterClosed().subscribe((submitted: boolean) => {
      if (submitted) {
        this.getData();
      }
    });
    return;
  }

  getData() {
    if (!this.searchLocal) {
      const pageEvent = this.getPageEvent(this.paginator);
      const sortEvent = this.getSortEvent(this.sort);
      const searchEvent = this.getSearchEvent(this.searchTerms);
      const filterEvent = this.getFilterEvent(this.advancedFilter, this.dateFilter, this.optionFilter);
      if (pageEvent && this.canSelect) {
        this.selectedRow = {};
        this.selectedRowEmit.emit({ row: this.selectedRow });
      }
      this.getTableData.emit({
        pageEvent,
        sortEvent,
        searchEvent,
        filterEvent,
      });
    } else {
      const pageEvent = this.getPageEvent(this.paginator);
      const filterEvent = this.getFilterEvent(this.advancedFilter, this.dateFilter, this.optionFilter);
      const sortEvent = this.getSortEvent(this.sort);

      if (pageEvent) {
        if (this.canSelect) {
          this.selectedRow = {};
          this.selectedRowEmit.emit({ row: this.selectedRow });
        }
        this.getPage.emit(pageEvent);
      }
      if (filterEvent) {
        this.getAdvancedFilter.emit(filterEvent);
      }
      this.getSort.emit(sortEvent);
    }
    this.scrollToTop();
  }

  scrollToTop() {
    const top = document.getElementById('table');
    top.scrollIntoView();
  }

  onColumnSorted() {
    if (this.checkNoAdvancedFiltering(this.sort)) {
      return;
    }

    this.resetPaginator();
    this.getData();
  }

  checkNoAdvancedFiltering(sort: MatSort) {
    if (!sort) {
      return true;
    }
    
    const { active: sortedCol } = this.sort;
    const sortedColConfig = this.tableConfig.find(({ field }) => field === sortedCol);

    if (!sortedColConfig) {
      return true;
    }
    
    return sortedColConfig.noAdvanceFiltering;
  }

  setField(obj: object, field: string): any {
    return get(obj, field);
  }
  
  setArrayField(arrayElement: any[]): string {
    if (!arrayElement) {
      return;
    }
    let arrString = "";
    if (arrayElement.length === 1) {
      arrString = `${arrayElement[0].arrayDescription}`;
    } else {
      arrString = `${this.translate.instant("CustomGrid.multiValues")} (${arrayElement.length})`;
    }
    return arrString;
  }

  setObjectField(objectElement: object): string {
    if (!objectElement) {
      return;
    }
    let objString = "";
    if (Object.entries(objectElement).length === 1) {
      objString = `${Object.keys(objectElement)[0]}: ${Object.values(objectElement)[0]}`;
    } else {
      objString = `${this.translate.instant("CustomGrid.multiValues")} (${Object.entries(objectElement).length})`;
    }
    return objString;
  }

  checkObjLength(objectElement: object): boolean {
    return Object.entries(objectElement).length > 1;
  }

  checkPinnedColumns(field: string): boolean {
    if (!this.pinnedColumns || !this.pinnedColumns.length) {
      return false;
    }

    return !!this.pinnedColumns.find((pinColId) => pinColId === field);
  }

  onSearchChanged(searchTerms: string[]) {
    if (!this.searchLocal) {
      this.searchTerms = [...searchTerms];
      this.resetPaginator();
      this.getData();
    } else {
      const searchEvent = this.getSearchEvent(searchTerms);
      this.getSearchTerms.emit(searchEvent);
    }
  }

  resetPaginator() {
    if (!this.paginator) {
      return;
    }
    this.paginator.pageIndex = 0;
  }

  getPageEvent(paginator: MatPaginator) {
    return {
      pageIndex: paginator?.pageIndex || DefaultPage.PAGE_INDEX,
      pageSize: paginator?.pageSize || DefaultPage.PAGE_SIZE,
    };
  }

  getSortEvent(sort: MatSort) {
    if (this.checkNoAdvancedFiltering(sort)) {
      return;
    }

    return sort?.active && sort?.direction
      ? { active: sort.active, direction: sort.direction }
      : undefined;
  }

  getSearchEvent(searchTerms: string[]) {
    return searchTerms.length
      ? {
          searchTerms: [...searchTerms],
          filterConfig: this.tableConfig.filter(({ searchable }) => searchable),
        }
      : undefined;
  }

  getFilterEvent(advancedFilter: AdvancedFilter[], dateFilter: AdvancedFilter[], optionFilter: AdvancedFilter[]) {
    const advancedFiltersWithDateFilters = [...advancedFilter, ...dateFilter, ...optionFilter]

    return advancedFiltersWithDateFilters.length
      ? { advancedFilter: advancedFiltersWithDateFilters }
      : undefined;
  }

  getExcelData(exportFormat: ExportFormat, exportLimit: ExportLimit) {
    if (!this.dataSource.data.length) {
      this.snackbarService.show({
        message: this.translate.instant("Grid.generateExcelNoData"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    this.snackbarService.show({
      message: this.translate.instant("Grid.generateExcel"),
      type: SnackbarType.INFO,
    });

    this.selectedExportFormat = exportFormat;
    const pageEvent = exportLimit === ExportLimit.CURRENT ? this.getPageEvent(this.paginator) : null;
    const sortEvent = this.getSortEvent(this.sort);
    const searchEvent = this.getSearchEvent(this.searchTerms);
    const filterEvent = this.getFilterEvent(this.advancedFilter, this.dateFilter, this.optionFilter);

    this.getExportData.emit({ pageEvent, sortEvent, searchEvent, filterEvent });
  }

  exportExcel(excelData: any[], exportFormat: ExportFormat) {
    if (!excelData) {
      return;
    }

    const treatedExcel = this.treatExcelData(excelData, this.tableConfig);

    const now = new Date();

    let formattedDate = now.toISOString().replace(/:/g, '').replace('T', '_').split('.')[0];
    formattedDate = formattedDate.replace(/-/g, '_');

    const formattedTableTitle = this.tableTitle.replace(' ', '_');
    
    let excelName = `${formattedTableTitle}_${formattedDate}`;
    if (excelName.length > 120) {
      excelName = excelName.substring(0, 120);
    }

    let sheetName = formattedTableTitle;
    if (sheetName.length > 31) {
      sheetName = sheetName.substring(0, 31);
    }

    const workSheet = XLSX.utils.json_to_sheet(treatedExcel, {
      dateNF: "DD/MM/YYYY HH:mm:ss",
      cellDates: true,
    });
    const [firstExcelRow] = treatedExcel;
    workSheet["!autofilter"] = {
      ref: "A1:" + numToSSColumn(Object.keys(firstExcelRow).length) + "1",
    };
    const workBook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, sheetName);
    XLSX.writeFile(workBook, `${excelName}.${exportFormat}`, {
      cellDates: true,
    });
  }

  treatExcelData(excelData: any[], tableConfig: TableConfig[]) {
    const filteredSortedTableConfig =
      this.getFilteredAndSortedTableConfig(tableConfig);

    return excelData.map((excelRow) => {
      let treatedObj = {};
      filteredSortedTableConfig.forEach(({ header, field, fieldType, objectDescription }) => {
        //Special case for manual transaction card codes
        if (field === 'cardCodes[1].code' && excelRow.cardCodes) {
          treatedObj = this.cardCodesSpecialTreatment(header, excelRow, 0, treatedObj);
          return;
        } else if (field === 'cardCodes[2].code' && excelRow.cardCodes) {
          treatedObj = this.cardCodesSpecialTreatment(header, excelRow, 1, treatedObj);
          return;
        }
        //Special case for manual transaction prompts
        if (field === 'promptsData[0].prompts' && excelRow.promptsData) {
          treatedObj = this.promptsSpecialTreatment(header, excelRow, 0, treatedObj);
          return;
        } else if (field === 'promptsData[1].prompts' && excelRow.promptsData) {
          treatedObj = this.promptsSpecialTreatment(header, excelRow, 1, treatedObj);
          return;
        }
        //Special case for transaction prompts
        if (field === 'debitMovement.card[1].promptData' && excelRow.debitMovement.card) {
          treatedObj = this.promptsSpecialTreatment2(header, excelRow, 1, treatedObj);
          return;
        } else if (field === 'debitMovement.card[2].promptData' && excelRow.debitMovement.card) {
          treatedObj = this.promptsSpecialTreatment2(header, excelRow, 2, treatedObj);
          return;
        }
        const nestedField = this.setField(excelRow, field);
        treatedObj = {
          ...treatedObj,
          [this.translate.instant(header)]: this.handleExcelObject(
            fieldType,
            nestedField,
            objectDescription
          ),
        };
      });
      return treatedObj;
    });
  }
  
  cardCodesSpecialTreatment(header: string, excelRow: any, index: number, treatedObj: Object) {
    if (!excelRow.cardCodes[index]) {
      return treatedObj;
    }
    const nestedField = excelRow.cardCodes[index];
    return {
      ...treatedObj,
      [this.translate.instant(header)]: this.handleExcelObject(
        FieldType.STRING,
        nestedField
      ),
    };
  }

  promptsSpecialTreatment(header: string, excelRow: any, index: number, treatedObj: Object) {
    if (!excelRow.promptsData[index] || !excelRow.promptsData[index].prompts) {
      return treatedObj;
    }
    const nestedFieldObjs = excelRow.promptsData[index].prompts;
    const promptsFinalString = nestedFieldObjs.reduce((acc, cur) => {
      let prompts = '';
      for (const [key, value] of Object.entries(cur)) {
        if (prompts.length) {
          prompts = `${prompts} | ${key}: ${value}`;
        } else {
          prompts = `${key}: ${value}`;
        }
      }
      if (acc.length) {
        return `${acc} | ${prompts}`; 
      }
      return `${prompts}`;
    }, '');
    return {
      ...treatedObj,
      [this.translate.instant(header)]: this.handleExcelObject(
        FieldType.STRING,
        promptsFinalString
      ),
    };
  }

  promptsSpecialTreatment2(header: string, excelRow: any, index: number, treatedObj: Object) {
    if (!excelRow.debitMovement.card[index] || !excelRow.debitMovement.card[index].promptData) {
      return treatedObj;
    }
    const nestedFieldObj = excelRow.debitMovement.card[index].promptData;
    let promptsFinalString = '';
    for (const [key, value] of Object.entries(nestedFieldObj)) {
      if (promptsFinalString.length) {
        promptsFinalString = `${promptsFinalString} | ${key}: ${value}`;
      } else {
        promptsFinalString = `${key}: ${value}`;
      }
    }
    return {
      ...treatedObj,
      [this.translate.instant(header)]: this.handleExcelObject(
        FieldType.STRING,
        promptsFinalString
      ),
    };
  }

  handleExcelObject(fieldType: FieldType, nestedField: any, objectDescription = false) {
    switch (fieldType) {
      case FieldType.DATE:
        if (nestedField && Date.parse(nestedField)) {
          return new Date(nestedField).toLocaleDateString();
        } else return null;
      case FieldType.DATE_TIME:
        if (nestedField && Date.parse(nestedField)) {
          return new Date(nestedField);
        } else return null;
      case FieldType.BOOLEAN:
        if (nestedField) {
          return this.translate.instant("CustomGrid.True");
        } else {
          return this.translate.instant("CustomGrid.False");
        }
      case FieldType.ARRAY:
        if (!nestedField) {
          return null;
        }
        if (nestedField.length === 1) {
          return nestedField[0].arrayDescription;
        } else if (nestedField.length > 1) {
          return nestedField.reduce((acc, cur) => {
            if (acc.length) {
              return `${acc} | ${cur.arrayDescription}`; 
            }
            return `${cur.arrayDescription}`;
          }, '');
        }
      case FieldType.OBJECT_DESCRIPTION:
        if (nestedField) {
          return nestedField[this.locale];
        }
      default:
        return objectDescription ? nestedField[this.locale] : nestedField;
    }
  }

  getFilteredAndSortedTableConfig(tableConfig: TableConfig[]) {
    return tableConfig
      .filter(
        ({ field, fieldType }) =>
          fieldType !== FieldType.OPTIONS &&
          fieldType !== FieldType.PROGRESS &&
          this.displayedColumns.includes(field)
      )
      .sort(
        (a, b) =>
          this.displayedColumns.indexOf(a.field) -
          this.displayedColumns.indexOf(b.field)
      );
  }

  onTableConfigSaved(columnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig.emit(columnInfo);
  }

  onAdvancedFilterChanged(advancedFilter: AdvancedFilter[]) {
    this.advancedFilter = advancedFilter;

    this.resetPaginator();
    this.getData();
  }

  checkAppliedFilter(column: TableConfig): boolean {
    const advancedFiltersWithDateFilters = [...this.advancedFilter, ...this.dateFilter, ...this.optionFilter];
    
    return advancedFiltersWithDateFilters.some((filter) => column.field === filter.column);
  }

  ngOnDestroy(): void {
    this.langSubscription.unsubscribe();
  }
}
